import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Safe date formatting function
const formatDate = (date, formatStr) => {
  try {
    if (!date) {
      date = new Date();
    }
    
    // Simple format conversions
    if (formatStr === 'MMM dd, yyyy') {
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: '2-digit' 
      });
    } else if (formatStr === 'yyyyMMdd') {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}${month}${day}`;
    } else if (formatStr === 'MMM dd, yyyy HH:mm') {
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: '2-digit' 
      }) + ' ' + date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
    
    // Default fallback
    return date.toLocaleDateString();
  } catch (error) {
    console.warn('Date formatting failed:', error);
    return new Date().toLocaleDateString();
  }
};

// Add error handling for PDF generation
const handlePDFError = (error, operation) => {
  console.error(`PDF ${operation} failed:`, error);
  alert(`PDF ${operation} failed. Please try again or contact support.`);
};



/**
 * Generates a PDF bill for the given bill data
 * @param {Object} billData - The bill data object
 * @param {string} billId - The bill ID
 */
export const generateBillPDF = (billData, billId) => {
  try {
    console.log('Generating PDF for bill:', billId, billData);
    
    // Validate input data
    if (!billData) {
      throw new Error('Bill data is required');
    }
    if (!billId) {
      throw new Error('Bill ID is required');
    }

    const doc = new jsPDF();
    
    // Company colors (using the brand primary color)
    const primaryColor = [253, 93, 134]; // #FD5D86
    const darkGray = [64, 64, 64];
    const lightGray = [128, 128, 128];
    
    // Header Section
    doc.setFillColor(253, 93, 134);
    doc.rect(0, 0, 210, 30, 'F');
    
    // Company Name
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(24);
    doc.text('LITTLO', 20, 20);
    
    // Bill Title
    doc.setFontSize(14);
    doc.text('INVOICE', 160, 15);
    doc.setFontSize(10);
    doc.text(`Bill #${String(billId).substring(0, 8)}`, 160, 22);
    
    // Reset text color
    doc.setTextColor(64, 64, 64);
    
    // Company Information
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text('Photography & Props Rental Services', 20, 40);
    doc.text('Professional Photography Solutions', 20, 46);
    
    // Bill Date
    let billDate;
    try {
      billDate = billData.createdAt?.toDate ? 
        formatDate(billData.createdAt.toDate(), 'MMM dd, yyyy') : 
        formatDate(new Date(), 'MMM dd, yyyy');
    } catch (dateError) {
      console.warn('Date formatting error:', dateError);
      billDate = new Date().toLocaleDateString();
    }
    doc.text(`Date: ${billDate}`, 160, 40);
    
    // Customer Information Box
    doc.setFillColor(248, 249, 250);
    doc.rect(20, 55, 85, 30, 'F');
    doc.setDrawColor(200, 200, 200);
    doc.rect(20, 55, 85, 30);
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('BILL TO:', 25, 65);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Name: ${billData.customerName || 'N/A'}`, 25, 75);
    doc.text(`Phone: ${billData.customerPhone || 'N/A'}`, 25, 82);
    
    // Payment Status Box
    doc.setFillColor(248, 249, 250);
    doc.rect(115, 55, 75, 30, 'F');
    doc.setDrawColor(200, 200, 200);
    doc.rect(115, 55, 75, 30);
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('PAYMENT STATUS:', 120, 65);
    
    // Status color based on payment status
    const status = billData.status || 'pending';
    const statusColor = status === 'paid' ? [34, 197, 94] : 
                       status === 'pending' ? [251, 191, 36] : [239, 68, 68];
    doc.setTextColor(statusColor[0], statusColor[1], statusColor[2]);
    doc.setFont('helvetica', 'bold');
    doc.text(status.toUpperCase(), 120, 75);
    doc.setTextColor(64, 64, 64);
    
    if (billData.paymentMethod) {
      doc.setFont('helvetica', 'normal');
      doc.text(`Method: ${billData.paymentMethod}`, 120, 82);
    }
    
    // Items Table - Simplified approach without autoTable first
    let currentY = 95;
    
    // Table Header
    doc.setFillColor(253, 93, 134);
    doc.rect(20, currentY, 170, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Item/Service', 25, currentY + 7);
    doc.text('Type', 90, currentY + 7);
    doc.text('Qty', 125, currentY + 7);
    doc.text('Rate', 145, currentY + 7);
    doc.text('Total', 170, currentY + 7);
    
    currentY += 10;
    doc.setTextColor(64, 64, 64);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    
    // Table Body
    const items = billData.items || [];
    console.log('Processing items for PDF:', items);
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const isEven = i % 2 === 0;
      
      if (isEven) {
        doc.setFillColor(248, 249, 250);
        doc.rect(20, currentY, 170, 8, 'F');
      }
      
      doc.text(String(item.name || 'Unknown Item'), 25, currentY + 5);
      doc.text(item.type === 'props' ? 'Props Rental' : 'Photography Package', 90, currentY + 5);
      doc.text(String(item.quantity || 1), 125, currentY + 5);
      doc.text(`₹${(item.rate || 0).toLocaleString()}`, 145, currentY + 5);
      doc.text(`₹${(item.total || 0).toLocaleString()}`, 170, currentY + 5);
      
      currentY += 8;
    }
    
    // If no items, show message
    if (items.length === 0) {
      doc.text('No items found', 25, currentY + 5);
      currentY += 8;
    }
    
    // Table border
    doc.setDrawColor(200, 200, 200);
    doc.rect(20, 95, 170, currentY - 95);
    
    // Total Section
    currentY += 10;
    
    // Total amount box
    doc.setFillColor(253, 93, 134);
    doc.rect(130, currentY, 60, 20, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('TOTAL AMOUNT', 135, currentY + 8);
    doc.setFontSize(16);
    doc.text(`₹${(billData.total || 0).toLocaleString()}`, 135, currentY + 16);
    
    // Footer
    const pageHeight = doc.internal.pageSize.height;
    doc.setTextColor(128, 128, 128);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('Thank you for choosing Littlo!', 20, pageHeight - 20);
    doc.text('For support, contact us at your registered phone number.', 20, pageHeight - 15);
    
    let footerDate;
    try {
      footerDate = formatDate(new Date(), 'MMM dd, yyyy HH:mm');
    } catch (error) {
      footerDate = new Date().toLocaleDateString() + ' ' + new Date().toLocaleTimeString();
    }
    doc.text(`Generated on ${footerDate}`, 20, pageHeight - 10);
    
    // Add page border
    doc.setDrawColor(253, 93, 134);
    doc.setLineWidth(0.5);
    doc.rect(10, 10, 190, pageHeight - 20);
    
    console.log('PDF generated successfully');
    return doc;
  } catch (error) {
    console.error('PDF generation error:', error);
    handlePDFError(error, 'generation');
    throw error;
  }
};

/**
 * Downloads the PDF bill
 * @param {Object} billData - The bill data object
 * @param {string} billId - The bill ID
 */
export const downloadBillPDF = (billData, billId) => {
  try {
    console.log('Starting PDF download for bill:', billId);
    const doc = generateBillPDF(billData, billId);
    const fileName = `Littlo_Bill_${String(billId).substring(0, 8)}_${formatDate(new Date(), 'yyyyMMdd')}.pdf`;
    doc.save(fileName);
    console.log('PDF download initiated:', fileName);
  } catch (error) {
    handlePDFError(error, 'download');
  }
};

/**
 * Opens the PDF in a new window for viewing/printing
 * @param {Object} billData - The bill data object
 * @param {string} billId - The bill ID
 */
export const viewBillPDF = (billData, billId) => {
  try {
    console.log('Starting PDF view for bill:', billId);
    const doc = generateBillPDF(billData, billId);
    const pdfUrl = doc.output('bloburl');
    window.open(pdfUrl, '_blank');
    console.log('PDF view opened in new tab');
  } catch (error) {
    handlePDFError(error, 'view');
  }
};
